import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useFees } from '../context/FeeContext';
import InlineChatPopup from './InlineChatPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderCalendar = ({ providerId, isOwnCalendar = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { feeRates } = useFees();
  const providerFeeRate = feeRates.provider_fee_rate / 100; // Convert to decimal
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayBookings, setDayBookings] = useState([]);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [chatPopup, setChatPopup] = useState({ open: false, clientId: null, clientName: null, serviceId: null, serviceName: null });

  // Get today's date at midnight for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, currentDate]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API}/provider/${providerId}/bookings?year=${year}&month=${month}`);
      setBookings(response.data.bookings || []);
      setAvailability(response.data.availability || {});
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const getBookingsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(b => {
      const bookDate = b.date || b.scheduled_date;
      if (!bookDate) return false;
      // Exact date match
      if (bookDate === dateStr || (b.scheduled_date && b.scheduled_date.startsWith(dateStr))) return true;
      // Multi-day booking: check if dateStr falls within booking range
      if (b.end_date && bookDate <= dateStr && b.end_date >= dateStr) return true;
      if (b.requested_days && b.requested_days > 1 && bookDate) {
        try {
          const startDt = new Date(bookDate + 'T00:00:00');
          const endDt = new Date(startDt);
          endDt.setDate(endDt.getDate() + b.requested_days - 1);
          const endStr = `${endDt.getFullYear()}-${String(endDt.getMonth() + 1).padStart(2, '0')}-${String(endDt.getDate()).padStart(2, '0')}`;
          if (dateStr >= bookDate && dateStr <= endStr) return true;
        } catch(e) {}
      }
      return false;
    });
  };

  // Check if a date is in the past
  const isPastDate = (day) => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Check if a date is blocked
  const isBlockedDate = (day) => {
    if (!availability.blocked_dates) return false;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return availability.blocked_dates.some(bd => bd.date === dateStr);
  };

  // Check if a date is in a blocked project range
  const isInBlockedProjectRange = (day) => {
    if (!availability.blocked_date_ranges || availability.blocked_date_ranges.length === 0) return null;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    for (const range of availability.blocked_date_ranges) {
      if (dateStr >= range.start_date && dateStr <= range.end_date) {
        return range; // Return the blocking range
      }
    }
    return null;
  };

  // Get blocked project reason for a day
  const getBlockedProjectReason = (day) => {
    const range = isInBlockedProjectRange(day);
    return range ? range.reason : null;
  };

  // Check if a day of week is a working day
  const isWorkingDay = (day) => {
    if (!availability.working_hours || availability.working_hours.length === 0) return true;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[date.getDay()];
    const workingHour = availability.working_hours.find(wh => wh.day === dayOfWeek);
    return workingHour ? workingHour.is_working : true;
  };

  // Get blocked date reason
  const getBlockedReason = (day) => {
    if (!availability.blocked_dates) return null;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const blocked = availability.blocked_dates.find(bd => bd.date === dateStr);
    return blocked?.reason || 'Blocked';
  };

  const getDayStatus = (day) => {
    const dateBookings = getBookingsForDate(day);
    const isPast = isPastDate(day);
    
    // For past dates, still check if there are completed bookings to show
    if (isPast) {
      // If there are completed bookings on this past date, mark as 'past_with_bookings'
      const hasCompletedBookings = dateBookings.some(b => b.status === 'completed');
      if (hasCompletedBookings) return 'past_with_bookings';
      return 'past';
    }
    
    // Check if it's a blocked date
    if (isBlockedDate(day)) return 'blocked';
    
    // Check if it's in a blocked project range
    if (isInBlockedProjectRange(day)) return 'blocked_project';
    
    // Check if it's a non-working day (closed)
    if (!isWorkingDay(day)) return 'closed';
    
    // Check bookings
    if (dateBookings.length === 0) return 'available';
    
    const acceptedBookings = dateBookings.filter(b => b.status === 'accepted' || b.status === 'in_progress');
    const hasCompleted = dateBookings.some(b => b.status === 'completed');
    const hasPending = dateBookings.some(b => b.status === 'pending');
    
    // Check if provider has multiple time slots and some are still available
    // If there are bookings but with scheduled times, mark as partially_booked
    if (acceptedBookings.length > 0) {
      // Check if all bookings have specific times (meaning other times might be available)
      const bookingsWithTime = acceptedBookings.filter(b => b.scheduled_time);
      if (bookingsWithTime.length === acceptedBookings.length && acceptedBookings.length < 3) {
        // Has time-specific bookings, still partially available
        return 'partially_booked';
      }
      return 'booked';
    }
    if (hasPending) return 'pending';
    if (hasCompleted) return 'completed';
    return 'available';
  };

  // Get booking count for a day (for display)
  const getBookingCount = (day) => {
    const dateBookings = getBookingsForDate(day);
    return dateBookings.filter(b => b.status === 'accepted' || b.status === 'in_progress').length;
  };

  const handleDateClick = (day) => {
    const dateBookings = getBookingsForDate(day);
    setSelectedDate(day);
    setDayBookings(dateBookings.sort((a, b) => {
      // Sort by time if available
      if (a.scheduled_time && b.scheduled_time) {
        return a.scheduled_time.localeCompare(b.scheduled_time);
      }
      return 0;
    }));
  };

  const prevMonth = () => {
    if (viewMode === 'week') {
      const d = new Date(currentDate);
      d.setDate(d.getDate() - 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    }
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (viewMode === 'week') {
      const d = new Date(currentDate);
      d.setDate(d.getDate() + 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    }
    setSelectedDate(null);
  };

  const formatTime = (time) => {
    if (!time) return '';
    // Convert 24h to 12h format
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
      case 'in_progress':
        return 'border-l-green-500 bg-green-50';
      case 'pending':
        return 'border-l-yellow-500 bg-yellow-50';
      case 'completed':
        return 'border-l-blue-500 bg-blue-50';
      case 'pending_completion':
        return 'border-l-purple-500 bg-purple-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'accepted': 'bg-green-100 text-green-800',
      'in_progress': 'bg-purple-100 text-purple-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-blue-100 text-blue-800',
      'pending_completion': 'bg-purple-100 text-purple-800',
      'rejected': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const statusColors = {
    available: 'bg-green-50 hover:bg-green-100 text-green-800 border border-green-200 cursor-pointer',
    booked: 'bg-red-50 text-red-800 cursor-pointer hover:bg-red-100 border border-red-200',
    partially_booked: 'bg-amber-50 text-amber-800 cursor-pointer hover:bg-amber-100 border border-amber-300',
    pending: 'bg-yellow-50 text-yellow-800 cursor-pointer hover:bg-yellow-100 border border-yellow-200',
    completed: 'bg-blue-50 text-blue-800 cursor-pointer hover:bg-blue-100 border border-blue-200',
    past: 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-60',
    past_with_bookings: 'bg-blue-50 text-blue-600 cursor-pointer hover:bg-blue-100 border border-blue-200 opacity-80',
    blocked: 'bg-red-100 text-red-400 border border-red-300 cursor-not-allowed',
    blocked_project: 'bg-purple-100 text-purple-600 border border-purple-300 cursor-pointer hover:bg-purple-200',
    closed: 'bg-gray-200 text-gray-500 border border-gray-300 cursor-not-allowed'
  };

  // Get week view data
  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getBookingsForFullDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(b => b.date === dateStr || (b.scheduled_date && b.scheduled_date.startsWith(dateStr)));
  };

  return (
    <>
    <div className="bg-white rounded-lg shadow-md p-4" data-testid="provider-calendar">
      {/* Calendar Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={prevMonth}
            className="p-2 hover:bg-gray-100 rounded-full transition"
            data-testid="prev-month"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-base sm:text-lg font-bold min-w-[110px] sm:min-w-[180px] text-center">
            {viewMode === 'week' ? (() => {
              const weekDays = getWeekDays();
              const start = weekDays[0];
              const end = weekDays[6];
              if (start.getMonth() === end.getMonth()) {
                return `${monthNames[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
              }
              return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
            })() : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
          </h3>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-gray-100 rounded-full transition"
            data-testid="next-month"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('month')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition ${
              viewMode === 'month' ? 'bg-white shadow text-blue-600' : 'text-gray-600'
            }`}
            data-testid="view-month"
          >
            {t('ux.month')}
          </button>
          <button
            onClick={() => {
              setViewMode('week');
              setCurrentDate(new Date()); // Reset to current week
            }}
            className={`px-3 py-1 rounded-md text-sm font-medium transition ${
              viewMode === 'week' ? 'bg-white shadow text-blue-600' : 'text-gray-600'
            }`}
            data-testid="view-week"
          >
            {t('ux.week')}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-green-100 rounded border border-green-300"></div>
          <span>{t('ux.legAvailable')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-amber-100 rounded border border-amber-300"></div>
          <span>{t('ux.legPartially')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-red-100 rounded border border-red-300"></div>
          <span>{t('ux.legFully')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-yellow-100 rounded border border-yellow-300"></div>
          <span>{t('ux.legPending')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-blue-100 rounded border border-blue-300"></div>
          <span>{t('ux.legCompleted')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-gray-200 rounded border border-gray-300"></div>
          <span>{t('ux.legClosed')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-red-200 rounded border border-red-400"></div>
          <span>{t('ux.legBlocked')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-purple-100 rounded border border-purple-300"></div>
          <span>{t('ux.legProject')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-gray-100 rounded border border-gray-200 opacity-60"></div>
          <span>{t('ux.legPast')}</span>
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center text-gray-500">
          <div className="flex items-center gap-2">
            <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            Loading...
          </div>
        </div>
      ) : viewMode === 'month' ? (
        <>
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days - Month View */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before the month starts */}
            {Array.from({ length: startingDay }).map((_, i) => (
              <div key={`empty-${i}`} className="h-16"></div>
            ))}
            
            {/* Days of the month */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const status = getDayStatus(day);
              const dateBookings = getBookingsForDate(day);
              const isSelected = selectedDate === day;
              const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
              // Allow clicking on past dates that have completed bookings and project blocked dates
              const isDisabled = status === 'past' || status === 'blocked' || status === 'closed';
              const isClickable = !isDisabled || status === 'past_with_bookings' || status === 'blocked_project';
              const blockedReason = status === 'blocked' ? getBlockedReason(day) : null;
              const blockedProjectReason = status === 'blocked_project' ? getBlockedProjectReason(day) : null;
              
              return (
                <button
                  key={day}
                  onClick={() => isClickable && handleDateClick(day)}
                  disabled={!isClickable}
                  title={
                    status === 'past' ? 'Past date - no bookings' : 
                    status === 'past_with_bookings' ? 'View completed booking(s)' :
                    status === 'blocked' ? `Blocked: ${blockedReason}` : 
                    status === 'blocked_project' ? `${blockedProjectReason}` :
                    status === 'closed' ? 'Closed (non-working day)' : 
                    undefined
                  }
                  className={`min-h-[64px] rounded-lg flex flex-col items-center justify-start p-1 text-sm font-medium transition
                    ${statusColors[status]}
                    ${isSelected && isClickable ? 'ring-2 ring-blue-500' : ''}
                    ${isToday && isClickable ? 'ring-2 ring-purple-500' : ''}
                  `}
                  data-testid={`calendar-day-${day}`}
                >
                  <span className={`${isToday ? 'bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center' : ''}`}>
                    {day}
                  </span>
                  {/* Show status indicator for blocked/closed/partially booked/project */}
                  {status === 'blocked' && (
                    <span className="text-[10px] mt-1 text-red-500 font-medium">Blocked</span>
                  )}
                  {status === 'blocked_project' && (
                    <span className="text-[10px] mt-1 text-purple-600 font-medium">📋 Project</span>
                  )}
                  {status === 'closed' && (
                    <span className="text-[10px] mt-1 text-gray-500 font-medium">Closed</span>
                  )}
                  {status === 'partially_booked' && (
                    <span className="text-[10px] mt-1 text-amber-600 font-medium">
                      {getBookingCount(day)} booked
                    </span>
                  )}
                  {status === 'past_with_bookings' && (
                    <span className="text-[10px] mt-1 text-blue-600 font-medium">
                      Completed
                    </span>
                  )}
                  {dateBookings.length > 0 && isClickable && (
                    <div className="mt-1 w-full space-y-0.5">
                      {dateBookings.slice(0, 2).map((b, idx) => (
                        <div 
                          key={idx} 
                          className={`text-[10px] truncate px-1 py-0.5 rounded ${getStatusBadge(b.status)}`}
                          title={`${b.scheduled_time ? formatTime(b.scheduled_time) + ' - ' : ''}${b.service_title || 'Booking'}`}
                        >
                          {b.scheduled_time && <span className="font-semibold">{formatTime(b.scheduled_time).split(' ')[0]} </span>}
                          {b.service_title?.substring(0, 8) || 'Booking'}
                        </div>
                      ))}
                      {dateBookings.length > 2 && (
                        <div className="text-[10px] text-gray-500 text-center">
                          +{dateBookings.length - 2} more
                        </div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        /* Week View */
        <div className="border rounded-lg overflow-hidden">
          {/* Week Day Headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b">
            {getWeekDays().map((date, idx) => {
              const isToday = new Date().toDateString() === date.toDateString();
              return (
                <div 
                  key={idx} 
                  className={`text-center py-2 border-r last:border-r-0 ${isToday ? 'bg-purple-50' : ''}`}
                >
                  <div className="text-xs text-gray-500">{dayNames[idx]}</div>
                  <div className={`text-lg font-bold ${isToday ? 'text-purple-600' : ''}`}>{date.getDate()}</div>
                </div>
              );
            })}
          </div>
          
          {/* Week Day Content - scrollable */}
          <div className="grid grid-cols-7 max-h-[400px] overflow-y-auto">
            {getWeekDays().map((date, idx) => {
              const dateBookings = getBookingsForFullDate(date);
              return (
                <div 
                  key={idx} 
                  className="border-r last:border-r-0 p-2 bg-white min-h-[120px]"
                >
                  {dateBookings.length === 0 ? (
                    <div className="text-xs text-gray-400 text-center mt-8">No bookings</div>
                  ) : (
                    <div className="space-y-2">
                      {dateBookings.sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || '')).map((booking, bIdx) => (
                        <button 
                          key={bIdx}
                          onClick={() => {
                            // Set selected date and show details panel
                            const day = date.getDate();
                            setSelectedDate(day);
                            setDayBookings(dateBookings.sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || '')));
                          }}
                          className={`w-full text-left p-2 rounded border-l-4 text-xs cursor-pointer hover:shadow-md transition ${getStatusColor(booking.status)}`}
                          data-testid={`week-booking-${bIdx}`}
                        >
                          {booking.scheduled_time && (
                            <div className="font-bold text-gray-800">
                              {formatTime(booking.scheduled_time)}
                            </div>
                          )}
                          <div className="font-medium truncate" title={booking.service_title}>
                            {booking.type === 'job' ? 'Job: ' : ''}{booking.service_title?.substring(0, 15) || 'Booking'}
                          </div>
                          <div className="text-gray-500 truncate">
                            {booking.client_name}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Day Details Panel */}
      {selectedDate && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border" data-testid="day-details">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-bold text-lg">
              {monthNames[currentDate.getMonth()]} {selectedDate}, {currentDate.getFullYear()}
            </h4>
            <button 
              onClick={() => setSelectedDate(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          
          {dayBookings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg">No bookings for this day</p>
              <p className="text-sm mt-1">This time slot is available</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Time-based schedule view */}
              <div className="text-sm text-gray-600 mb-2">
                {dayBookings.length} booking{dayBookings.length > 1 ? 's' : ''} scheduled
              </div>
              {dayBookings.map((booking, idx) => {
                const serviceAmount = booking.final_amount || booking.price || 0;
                const platformFee = serviceAmount * providerFeeRate;
                const providerEarnings = serviceAmount - platformFee;
                const isCompleted = booking.status === 'completed';
                
                return (
                <div 
                  key={idx} 
                  className={`bg-white p-4 rounded-lg border-l-4 shadow-sm ${getStatusColor(booking.status)}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {/* Type Badge */}
                      {booking.type === 'job' && (
                        <span className="inline-block bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium mb-2">
                          Job Booking
                        </span>
                      )}
                      {/* Time Badge */}
                      {(booking.scheduled_time || booking.time) && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-gray-800 text-white px-2 py-1 rounded text-sm font-bold">
                            {formatTime(booking.scheduled_time || booking.time)}
                          </span>
                          {booking.estimated_duration && (
                            <span className="text-gray-500 text-sm">
                              Est: {booking.estimated_duration}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Multi-day range info */}
                      {booking.requested_days > 1 && (
                        <div className="text-sm text-blue-600 mb-1 font-medium">
                          {booking.date} to {booking.end_date || 'N/A'} ({booking.requested_days} days)
                        </div>
                      )}
                      <p className="font-semibold text-gray-800">{booking.service_title || 'Service Booking'}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Client:</span>{' '}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (booking.client_id) {
                              setChatPopup({
                                open: true,
                                clientId: booking.client_id,
                                clientName: booking.client_name || 'Client',
                                serviceId: booking.service_id,
                                serviceName: booking.service_title || 'Booking',
                              });
                            }
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition"
                          data-testid={`client-chat-${booking.client_id}`}
                          title="Open chat with this client"
                        >
                          {booking.client_name || 'Unknown'}
                        </button>
                      </p>
                      {booking.message && (
                        <p className="text-sm text-gray-500 mt-2 italic">"{booking.message}"</p>
                      )}
                      {booking.notes && booking.type === 'job' && (
                        <p className="text-sm text-gray-500 mt-2 italic">"{booking.notes}"</p>
                      )}
                      
                      {/* Earnings Breakdown for Provider */}
                      {serviceAmount > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                            {isCompleted ? 'Earnings Breakdown' : 'Estimated Earnings'}
                          </p>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Service Amount:</span>
                              <span className="font-medium">₱{serviceAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                            </div>
                            <div className="flex justify-between text-red-600">
                              <span>Platform Fee ({feeRates.provider_fee_rate}%):</span>
                              <span>-₱{platformFee.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                            </div>
                            <div className="flex justify-between font-bold pt-1 border-t border-gray-200">
                              <span className="text-gray-800">{isCompleted ? 'Your Earnings:' : 'Est. Earnings:'}</span>
                              <span className="text-green-600">₱{providerEarnings.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadge(booking.status)}`}>
                      {booking.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
      {/* Inline Chat Popup */}
      <InlineChatPopup
        isOpen={chatPopup.open}
        onClose={() => setChatPopup({ open: false, clientId: null, clientName: null, serviceId: null, serviceName: null })}
        clientId={chatPopup.clientId}
        providerId={providerId}
        serviceId={chatPopup.serviceId}
        serviceName={chatPopup.serviceName}
        clientName={chatPopup.clientName}
        token={localStorage.getItem('token')}
      />
    </>
  );
};

export default ProviderCalendar;
